import React from 'react';
import Typography from '@material-ui/core/Typography';
import styled from 'styled-components';
import MuiContainer from '@material-ui/core/Container';
import { NextSeo } from 'next-seo';

const Container = styled(MuiContainer)`
  display: flex !important;
  flex-direction: column;
`;

const PrivacyPolicy = () => {
  return (
    <>
      <NextSeo
        title="Privacy Policy"
        description="We care about your privacy and are committed to protecting your personal data. This privacy statement will inform you how we handle your personal data, your privacy rights and how the law protects you. Please read this privacy statement carefully before using our Services."
        canonical="https://rentable.pk/privacy-policy"
        openGraph={{
          url: 'https://rentable.pk/privacy-policy',
          type: 'basic',
          title: 'Privacy Policy',
          description:
            'We care about your privacy and are committed to protecting your personal data. This privacy statement will inform you how we handle your personal data, your privacy rights and how the law protects you. Please read this privacy statement carefully before using our Services.',
          images: [
            {
              url: 'https://rentable.pk/assets/logos/green-text-logo.png',
              alt: 'Rentable Pakistan',
            },
          ],
        }}
      />
      <Container maxWidth="lg">
        <Typography style={{ marginTop: 30 }} variant="h3" color="primary">
          Privacy Policy
        </Typography>
        <Typography variant="subtitle1">
          <em>Last updated on 25 December, 2019</em>
        </Typography>

        <Typography variant="body1" style={{ marginTop: 30 }}>
          <p>
            We care about your privacy and are committed to protecting your personal data. This
            privacy statement will inform you how we handle your personal data, your privacy rights
            and how the law protects you. Please read this privacy statement carefully before using
            our Services.
          </p>
          <strong>In this privacy statement:</strong>
          <p>
            <li>
              Services means any products, services, content, features, technologies, or functions,
              and all related websites, applications and services offered to you by us.
            </li>
            <li>
              Platform means the websites, mobile apps, mobile sites or other online properties
              through which we offer our Services.
            </li>
          </p>
        </Typography>
        <Typography variant="h4">Who We Are?</Typography>
        <Typography variant="body1">
          <p>
            The principal controller of your data for purposes of our international services is
            Rentable Pakistan, a private limited company with its office at Islamabad, Pakistan.
            Rentable PK is providing the services to you according to our Terms of Use, (hereinafter
            collectively referred to as “Rentable”, “we”, “us” or “our” in this privacy statement).
            These Rentable entities are also considered data controllers for the local services they
            offer. Relevant contact details are set out in contact section.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          What data do we collect about you?
        </Typography>
        <Typography variant="body1">
          <h4>Data provided through direct interactions</h4>
          <strong>Registration and other account information</strong>
          <p>
            When you register to use our Services we may collect the following information about
            you:
          </p>
          <ul>
            <li>
              If you register using your Google account: first name, last name and email address;
            </li>
            <li>
              If you register using your Facebook account: we collect first name and last name as
              appeared on your Facebook account and Facebook IDs. In the event you have provided
              permission to Facebook through their in-app privacy option (which appears just before
              you register on our Platform), we may collect your gender, age or email id depending
              on the permissions granted by you; and
            </li>
            <li>If you register using your mobile number: mobile number.</li>
          </ul>
          <p>
            Depending on the choices you make during the log-in to our Services or during the
            process of engaging our Services, you may opt to give the following additional personal
            data:
          </p>
          <ul>
            <li>Your Full Name</li>
            <li>Your Email Address</li>
            <li>Your Contact Number</li>
          </ul>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Data we collect automatically when you use our services
        </Typography>
        <Typography variant="body1">
          <p>
            When you interact with our Platform or use our Services, we automatically collect the
            following information about you:
          </p>
          <strong>Device Information</strong>
          <li>
            We collect device-specific information such as operating system version and unique
            identifiers. For example, the name of the mobile network that you are using. We
            associate the device identifiers with your Rentable account.
          </li>
          <strong>Location Information</strong>
          <li>
            Depending on your device permissions, if you post an item on our Platform, we
            automatically collect and process information about your actual location. We use various
            technologies to determine location, including IP address, GPS, Wi-Fi access points and
            mobile towers. Your location data allows you to see user items near you and helps you in
            posting items within your location.
          </li>
          <strong>Client and Log Data</strong>
          <li>
            Technical details, including the Internet Protocol (IP) address of your device, time
            zone and operating system. We will also store your login information (registration date,
            date of last password change, date of last successful login), type and version of your
            browser.
          </li>
          <strong>Clickstream Data</strong>
          <li>
            We collect information about your activity on our Platform which includes the sites from
            which you accessed our Platform, date and time stamp of each visit, searches you have
            performed, listings or advertisement banners you clicked, your interaction with such
            advertisements or listings, duration of your visit and the order in which you visit the
            content on our Platform.
          </li>
          <strong>Cookies and Similar Technologies</strong>
          <p>
            We use cookies to manage our users’ sessions, to store your preferences language
            selection and deliver you relevant advertisements. `&quot;`Cookies`&quot;` are small
            text files transferred by a web server to the hard drive of your device. Cookies may be
            used to collect the date and time of your visit, your browsing history, your
            preferences, and your username. You can set your browser to refuse all or some cookies,
            or to alert you when websites set or access cookies. If you disable or refuse cookies,
            please note that some parts of our Services/Platform may become inaccessible or not
            function properly. For more information on the cookies we use, please see our Policy on
            Cookies and Similar Technologies, the terms of which are incorporated into, and form
            part of, this Privacy Policy by reference.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Data from third parties or publicly available sources
        </Typography>
        <Typography variant="body1">
          <p>
            We may receive personal data about you from various third parties and public sources. If
            you would like more information about this, please contact us using the relevant contact
            details set out in contact section.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Do we collect data from Children?
        </Typography>
        <Typography variant="body1">
          <p>
            Our Services are not intended for children under 16 and we do not knowingly collect data
            from anyone under 16. If we become aware that a person under 16 has provided us with
            personal data, we will delete it immediately. If you are resident in Pakistan and under
            21, we are not permitted to contract with you directly. If you are a resident in
            Pakistan and under 21 then, by agreeing to this Privacy Policy, you acknowledge and
            agree that your guardian has read and consented to its terms on your behalf. If we seek
            your consent to process your personal data for a specific purpose in accordance with
            this Privacy Policy, consent must be granted on your behalf by your guardian.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Why do we process your personal information?
        </Typography>
        <Typography variant="body1">
          <p>
            We will only use your personal data when the law allows us to. Most commonly, we will
            use your personal data in the following circumstances:
          </p>
          <li>
            Where we need to perform the contract we are about to enter into or have entered into
            with you.
          </li>
          <li>
            Where it is necessary for our legitimate interests to improve our Services and to
            provide you a safe and secure Platform.
          </li>
          <li>Where we need to comply with a legal or regulatory obligation.</li>
          <p>
            In certain circumstances, we may also process your personal data based on your consent.
            If we do this, we will let you know the purpose and the category of personal data to be
            processed at the time we seek your consent.
          </p>
          <p>We have set out below a description of the ways we use your personal data.</p>
          <li>For providing access and delivering Services through our Platform</li>
          <li>
            If you log in using your mobile number or email id, we use your first name and last
            name, mobile number and/or e-mail address to identify you as a user and provide access
            to our Platform.
          </li>
          <li>
            If you log in using your Facebook account, we use your first name and last name from
            your Facebook profile and the Facebook e-mail address to identify you as a user on our
            Platform and to provide you access to our Platform.
          </li>
          <p>
            The above log-information data is also used by us to deliver our Services to you in
            accordance with our Terms of Use.
          </p>
          <p>
            We use your e-mail address and mobile number (by SMS) to make suggestions and
            recommendations to you about our Services that may be of interest to you.
          </p>
          <p>
            We process the above information for adequate performance of our contract with you and
            on the basis of our legitimate interest in undertaking marketing activities to offer you
            Services that may be of your interest.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          For improving your experience on the platform
        </Typography>
        <Typography variant="body1">
          <p>We use clickstream data to:</p>
          <li>
            Offer you tailored content, such as giving you more relevant search results when using
            our Services
          </li>
          <li>
            To determine how much time you spend on our Platform and in what manner you navigate
            through our Platform in order to understand your interests and to improve our Services
            based on this data. For example, we may provide you with suggestions on content that you
            can visit based on the contents you have clicked; and
          </li>
          <li>
            to monitor and report the effectiveness of the campaign delivery to our business
            partners and for internal business analysis.
          </li>
          <p>We use your location data for the following purposes:</p>
          <li>
            to compile anonymous and aggregated information about the characteristics and behaviour
            of Rentable users, including for the purposes of business analysis, segmentation and
            development of anonymous profiles;
          </li>
          <li>
            to enhance the performance of our Services and to personalise the content we direct
            towards you. For example - with the help of location data we display ad listings which
            are in your vicinity to improve your buying experience; and
          </li>
          <li>
            to measure and monitor your interaction with the third-party advertisements banners we
            place on our Platform.
          </li>
          <p>
            With the help of your log-in information which includes your email id and phone number,
            we map the different devices (such as desktop, mobile, tablets) used by you to access
            our Platform. This allows us to associate your activity on our Platform across devices
            and helps us in providing you a seamless experience no matter which device you use.
          </p>
          <p>
            We process the above information on the basis of our legitimate interest to improve your
            experience on our Platform and for adequate performance of our contract with you.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          To provide you with a safe and secure platform
        </Typography>
        <Typography variant="body1">
          <li>
            We use your mobile number, log data and unique device identifiers to administer and
            protect our Platform (including troubleshooting, data analysis, testing, fraud
            prevention, system maintenance, support, reporting and hosting of data).
          </li>
          <li>
            We analyse your communications done through our chat feature for fraud prevention and to
            promote safety by blocking spam or abusive messages that may have been sent to you by
            any other user.
          </li>
          <p>
            We process the above information for adequate performance of our contract with you, to
            improve our services and on the basis of our legitimate interest to prevent fraud.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          How will we inform you about changes in our Privacy Policy?
        </Typography>
        <Typography variant="body1">
          <p>
            We may amend this privacy statement from time to time. We will post changes on this page
            and will let you know by e-mail or through our Platform. If you or your guardian (as the
            case may be) do not agree with the changes, you may close your account by going to
            account setting and select delete account.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Your rights
        </Typography>
        <Typography variant="body1">
          <p>
            You may have certain legal rights regarding your personal data depending on the country
            you are in or how we interact with you. You can learn more about this in this section.
          </p>
          <li>
            Right to request access to your personal data (commonly known as a “data subject access
            request”). This enables you to receive a copy of the personal data we hold about you and
            to check that we are lawfully processing it.
          </li>
          <li>
            Right to request correction of any of the data we have about you. This enables you to
            have any incomplete or inaccurate data we hold about you corrected, though we may need
            to verify the accuracy of the new data you provide to us.
          </li>
          <li>
            Right to withdraw your consent to the processing of your personal data at any time. This
            does not affect the legality of any processing we have already carried out based on the
            consent given previously.
          </li>
          <p>
            No fee usually required: You will not have to pay a fee to access your personal data (or
            to exercise any of the other rights). However, we may charge a reasonable fee if your
            request is clearly unfounded, repetitive or excessive. Alternatively, we may refuse to
            comply with your request in these circumstances.
          </p>
          <p>
            Time limit to respond: We try to respond to all legitimate requests within one month.
            Occasionally it may take us longer than a month if your request is particularly complex
            or you have made several requests. In this case, we will notify you and keep you
            updated.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Communication and Marketing
        </Typography>
        <Typography variant="body1">
          <p>
            We will communicate with you by email, SMS or in app notification in connection with our
            Services/Platform to confirm your registration, to inform you in case your ad listing
            has become live/expired and for other transactional messages in relation to our
            Services. As it is imperative for us to provide you such transactional messages you may
            not be able to opt-out of such messages.
          </p>
          <p>
            However, you can ask us to stop sending you marketing communication at any time by
            clicking on the opt-out link in the email or SMS sent to you or by reaching out us on
            info@rentable.pk
          </p>
          <p>You may receive marketing communications from us if you:</p>
          <li>have requested such information from us;</li>
          <li>use our Platform or Services;</li>
          <li>Provided us with your details when you entered a competition; or</li>
          <li>Registered for a promotion.</li>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Who do we share your data with?
        </Typography>
        <Typography variant="body1">
          <p>
            We may have to share your personal data with the parties set out below for the purposes
            set out in section 4 above.
          </p>
          <p>
            <strong>Corporate affiliates</strong>– we may share your data with other Rentable
            Pakistan which are located within as well as outside European Economic Area (“EEA”) and
            Pakistan and help us in providing business operation services such as product
            enhancements, customer support and fraud detection mechanism. Where the GDPR applies to
            personal data then any sharing of personal data within the Rentable Pakistan situated
            outside the EEA will always be subject safeguards as described in section 9 or to a data
            transfer agreement which clearly defines the obligations of the parties and ensures
            appropriate technical and organisational measures to protect your data.
          </p>
          <p>
            <strong>Third Party Service Providers:</strong>
            We use third party service providers to help us deliver certain aspect of our Services
            for example, cloud storage facilities such as Amazon Web Services and Microsoft Azure.
            Service providers may be located inside or outside of the EEA and Pakistan.
          </p>
          <p>
            We conduct checks on our third-party service providers and require them to respect the
            security of your personal data and to treat it in accordance with the law. We do not
            allow them to use your personal data for their own purposes and only permit them to
            process your personal data for specified purposes and in accordance with our
            instructions.
          </p>
          <p>
            <strong>Advertising and analytics providers:</strong>
            In order to improve our Services, we will sometimes share your non-identifiable
            information with analytics providers that help us analyse how people are using our
            Platform/Service. We share your information with them in non identifiable form for
            monitoring and reporting the effectiveness of the campaign delivery to our business
            partners and for internal business analysis. For further details about our advertisers
            and analytics provider please refer to our Policy for Cookies and similar Technologies.
          </p>
          <p>
            <strong>Law enforcement authorities</strong>, regulators and others: We may disclose
            your personal data to law enforcement authorities, regulators, governmental or public
            bodies and other relevant third parties to comply with any legal or regulatory
            requirements.
          </p>
          <p>
            We may choose to sell, transfer, or merge parts of our business or our assets.
            Alternatively, we may seek to acquire other businesses or merge with them. If a change
            happens to our business, then the new owners may use your personal data in the same way
            as set out in this privacy statement.
          </p>
          <p>
            <strong>Publicly available information:</strong>
            When you post an item for sale using our Services, you may choose to make certain
            personal information visible to other Rentable users. This may include your first name,
            last name, your email address, your location and your contact number. Please note, any
            information you provide to other users can always be shared by them with others so
            please exercise discretion in this respect.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Where do we store your data and for how long?
        </Typography>
        <Typography variant="body1">
          <p>
            The data we collect about you may be stored and processed inside as well as outside the
            EEA and Pakistan in secure servers in order to provide the best possible user
            experience. For example – for fast website or mobile application build up.
          </p>
          <p>
            We will only retain your personal data for as long as necessary to fulfill the purposes
            we collected it for, including for the purposes of satisfying any legal, accounting, or
            reporting requirements.
          </p>
          <p>
            To determine the appropriate retention period for personal data, we consider the amount,
            nature, and sensitivity of the personal data, the potential risk of harm from
            unauthorised use or disclosure of your personal data, the purposes for which we process
            your personal data and whether we can achieve those purposes through other means, and
            the applicable legal requirements.
          </p>
          <p>
            In case you have any queries in relation to the retention period of your data please
            contact us at info@rentable.pk.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Technical and organizational measures and processing security
        </Typography>
        <Typography variant="body1">
          <p>
            All the information we receive about you are stored on secure servers and we have
            implemented technical and organizational measures that are suitable and necessary to
            protect your personal data. Rentable continually evaluates the security of its network
            and adequacy of its internal information security program which is designed to (a) help
            secure your data against accidental or unlawful loss, access or disclosure, (b) identify
            reasonably foreseeable risks to the security of the Rentable network, and (c) minimize
            security risks, including through risk assessment and regular testing. In addition, we
            ensure that all payment data are encrypted using SSL technology.
          </p>
          <p>
            Please note, despite the measures we have implemented to protect your data, the transfer
            of data through the Internet or other open networks is never completely secure and there
            is a risk that your personal data may be accessed by unauthorized third parties.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Links to third-party websites
        </Typography>
        <Typography variant="body1">
          <p>
            Our Platform may contain links to third party websites or apps. If you click on one of
            these links, please note that each one will have its own privacy policy. We do not
            control these websites/apps and are not responsible for those policies. When you leave
            our Platform, we encourage you to read the privacy notice of every website you visit.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Contact
        </Typography>
        <Typography variant="body1">
          <p>
            For any additional information or to exercise your rights, please contact our Privacy
            Officer at info@rentable.pk, or go to the Help Section in our Platform.
          </p>
        </Typography>
        <Typography variant="h4" style={{ marginTop: 30 }}>
          Changes to this policy
        </Typography>
        <Typography variant="body1" style={{ marginBottom: 40 }}>
          <p>
            We may amend this Privacy Policy from time to time to keep it up to date with legal
            requirements and the way we operate our business. Please regularly check this page for
            the latest version of this Privacy Policy. If we make material changes to this Privacy
            Policy, we will seek to inform you by notice on our website or application, or email
            (`&quot;`Notice of Change`&quot;`).
          </p>
          <p>
            Your continued use of our Services following an amendment or Notice of Change indicates
            your or your guardian`&apos;`s acceptance (as the case may be) of any changes.
          </p>
        </Typography>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
